'use client'

import { CLUSTER_CONFIG, CLUSTER_KEYS, type ClusterKey, matchOpportunityToCluster } from '@/config/market-config'
import { HYPEREVM_PRODUCTS } from '@/features/evault-details/data/products'
import { useSharedUnifiedEarnOpportunities } from '@/features/unified-earn/logic/useUnifiedEarnOpportunities'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PlaceholderRow, type ProductCluster, ProductsTable } from './ClusterSection'
import { EarnHeroCard, EarnHeroSkeleton } from './EarnHeroCard'

/**
 * Clusters that render as tiles in the ProductsTable. Evault-sourced clusters
 * (e.g. `earn`) are excluded here because they render in `EarnHeroCard`
 * instead. Derived from `CLUSTER_KEYS` so adding a new elend cluster to
 * `HYPEREVM_PRODUCTS` auto-populates the tile grid.
 */
const PRODUCT_TABLE_KEYS: ClusterKey[] = CLUSTER_KEYS.filter((key) => {
  const product = HYPEREVM_PRODUCTS.get(`hypurrfi-${key}`)
  return product?.source !== 'evault'
})

/** Evault-backed clusters (currently just `earn`) — rendered in the hero, not the tile grid. */
const EARN_KEYS: ClusterKey[] = CLUSTER_KEYS.filter((key) => {
  const product = HYPEREVM_PRODUCTS.get(`hypurrfi-${key}`)
  return product?.source === 'evault'
})

const EXCLUDED_POOLED_MARKETS = new Set(
  [
    '0xb50a96253abdf803d85efcdce07ad8becbc52bd5',
    '0x311dB0FDe558689550c68355783c95eFDfe25329',
    '0x02c6a2fA58cC01A18B8D9E00eA48d65E4dF26c70',
    '0x211Cc4DD073734dA055fbF44a2b4667d5E5fE5d2',
    '0xea84ca9849D9e76a78B91F221F84e9Ca065FC9f5',
  ].map((address) => address.toLowerCase()),
)

function isProductClusterKey(value: string | null): value is ClusterKey {
  return value !== null && PRODUCT_TABLE_KEYS.includes(value)
}

function MarketsBelowHeaderFallback() {
  return (
    <div className="flex flex-col gap-8">
      <EarnHeroSkeleton />
      <div className="h-[400px] animate-pulse rounded-2xl bg-[#1a1a1e]" />
    </div>
  )
}

function MarketsOverviewContent() {
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const { opportunities, isLoading } = useSharedUnifiedEarnOpportunities()

  const [hasMounted, setHasMounted] = useState(false)
  const [selectedCluster, setSelectedCluster] = useState<ClusterKey | null>(null)
  const [scrollToCluster, setScrollToCluster] = useState<ClusterKey | null>(null)
  const appliedDefaultOpenRef = useRef<string | null>(null)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const earnOpportunities = useMemo(
    () =>
      opportunities
        .filter((opp) => EARN_KEYS.some((key) => matchOpportunityToCluster(opp, key)))
        .sort((a, b) => (b.tvlUsd?.toNumber() ?? 0) - (a.tvlUsd?.toNumber() ?? 0)),
    [opportunities],
  )

  const clusterData: ProductCluster[] = useMemo(
    () =>
      PRODUCT_TABLE_KEYS.map((key) => {
        const config = CLUSTER_CONFIG[key]!
        const matched = opportunities
          .filter((opportunity) => matchOpportunityToCluster(opportunity, key))
          .filter((opportunity) => key !== 'pooled' || !EXCLUDED_POOLED_MARKETS.has(opportunity.address.toLowerCase()))
          .sort((a, b) => (b.tvlUsd?.toNumber() ?? 0) - (a.tvlUsd?.toNumber() ?? 0))

        const highestApy = matched.reduce((max, o) => Math.max(max, o.supplyAPY ?? 0), 0)
        const highestApy30d = matched.reduce((max, o) => Math.max(max, o.supplyApy30d ?? 0), 0)
        const totalTVL = matched.reduce((sum, o) => sum + (o.tvlUsd?.toNumber() ?? 0), 0)
        const curatorMap = new Map<string, string>()
        for (const o of matched) {
          if (o.curatorName && !curatorMap.has(o.curatorName)) {
            curatorMap.set(o.curatorName, o.curatorLogo || '')
          }
        }
        const curators = Array.from(curatorMap, ([name, logo]) => ({ name, logo }))

        return {
          key: config.key,
          name: config.name,
          description: config.description,
          opportunities: matched,
          highestApy,
          highestApy30d,
          totalTVL,
          curators,
        }
      }),
    [opportunities],
  )

  const handleClusterClick = (key: ClusterKey) => {
    setScrollToCluster(null)
    setSelectedCluster((prev) => (prev === key ? null : key))
  }

  const defaultOpenCluster = searchParams.get('open') ?? searchParams.get('cluster')

  useEffect(() => {
    if (!isProductClusterKey(defaultOpenCluster)) return
    if (appliedDefaultOpenRef.current === defaultOpenCluster) return
    if (!clusterData.some((cluster) => cluster.key === defaultOpenCluster)) return

    appliedDefaultOpenRef.current = defaultOpenCluster
    setSelectedCluster(defaultOpenCluster)
    setScrollToCluster(defaultOpenCluster)

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('open')
    nextParams.delete('cluster')
    const nextQuery = nextParams.toString()
    router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname, { scroll: false })
  }, [clusterData, defaultOpenCluster, pathname, router, searchParams])

  const showLoadingState = !hasMounted || (isLoading && opportunities.length === 0)
  const shouldShowEarnSkeleton = earnOpportunities.length === 0

  if (showLoadingState) return <MarketsBelowHeaderFallback />

  return (
    <>
      {shouldShowEarnSkeleton ? (
        <div className="pb-7">
          <EarnHeroSkeleton />
        </div>
      ) : (
        <EarnHeroCard opportunities={earnOpportunities} />
      )}

      <div className="flex flex-col gap-4">
        <ProductsTable
          clusters={clusterData}
          selectedCluster={selectedCluster}
          scrollToCluster={scrollToCluster}
          onClusterClick={handleClusterClick}
          onClusterScrolled={() => setScrollToCluster(null)}
        />
        <PlaceholderRow />
      </div>
    </>
  )
}

export function MarketsOverviewSection() {
  return <MarketsOverviewContent />
}
